import { ChangeEvent, Dispatch, MouseEvent, SetStateAction, JSX } from 'react';
import * as R from 'ramda';
import { Row, Col, Form, InputGroup, DropdownButton, Dropdown } from 'react-bootstrap';
import { HiOutlinePlus } from 'react-icons/hi';
import { MdDelete, MdArrowCircleRight } from 'react-icons/md';
import { Plover } from '@blisspointmedia/bpm-types/dist/webTrafficOptimization';
import { Button } from '@Components/Button/Button';
import { IconButton } from '@Components/IconButton';
import { Size } from '@Components/designConstants';
import {
  WtoCookieOperation,
  WtoCookieOperationUrl,
  WtoRule,
  WtoVariant,
  WtoVariantSplit
} from '../../wto-form-context';
import { COOKIE_OPERATION } from './rules-form-modal-constants';

export const getCookieOperationUrls = (
  variantSplits: WtoVariantSplit[],
  wtoVariants: WtoVariant[]
): WtoCookieOperationUrl[] => {
  const selectedVariantIds = variantSplits
    .filter(variant => Boolean(variant.variant_id))
    .map(variant => variant.variant_id);
  return wtoVariants
    .filter(variantOption => selectedVariantIds.includes(variantOption.variant_id))
    .map(variant => ({
      cookie_variant_id: variant.variant_id,
      cookie_variant_url: variant.variant_url
    }));
};

interface CookieSectionProps {
  newRule: WtoRule;
  setNewRule: Dispatch<SetStateAction<WtoRule>>;
  wtoFormObj: Plover;
  readOnly: boolean;
  onInputChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    valueType?: string,
    idx?: number
  ) => void;
  onSelectChange: (
    event: unknown,
    keyName?: 'additional_utm' | 'additional_utm_operation' | 'variant_id' | 'cookie_operation',
    index?: number,
    index2?: number
  ) => void;
  onDeleteOperationClick: (
    e: MouseEvent<HTMLElement>,
    ruleType:
      | 'conditions'
      | 'cookie_operation'
      | 'additional_utm'
      | 'additional_utm_operation'
      | 'variant_splits',
    optrnIdx?: number
  ) => void;
  addParameterUpdatesRow: (ruleType: string) => void;
}

export const CookieSection = ({
  newRule,
  setNewRule,
  wtoFormObj,
  readOnly,
  onInputChange,
  onSelectChange,
  onDeleteOperationClick,
  addParameterUpdatesRow
}: CookieSectionProps) => {
  const renderCookieLogic = (utmOptrnObj: WtoCookieOperation, operationIndex: number) => {
    const logicSelections: JSX.Element[] = [];
    switch (utmOptrnObj.operation) {
      case 'Replace':
        logicSelections.push(
          <div key={logicSelections.length} className='logic-selection'>
            <Form.Group as={Col}>
              <InputGroup>
                <Form.Control
                  value={utmOptrnObj.cookie_key || ''}
                  disabled={readOnly}
                  data-type='cookie_operation'
                  placeholder='Key'
                  onChange={e => onInputChange(e, 'cookie_key', operationIndex)}
                />
                <Form.Control
                  value={utmOptrnObj.cookie_value || ''}
                  disabled={readOnly}
                  data-type='cookie_operation'
                  placeholder='Value'
                  onChange={e => onInputChange(e, 'cookie_value', operationIndex)}
                />
              </InputGroup>
            </Form.Group>
            <div className='icon-wrapper'>
              <MdArrowCircleRight />
            </div>
            <Form.Group as={Col}>
              <InputGroup>
                <Form.Control
                  value={utmOptrnObj.cki_replace_key || ''}
                  disabled={readOnly}
                  data-type='cookie_operation'
                  placeholder='Key'
                  onChange={e => onInputChange(e, 'cki_replace_key', operationIndex)}
                />
                <Form.Control
                  value={utmOptrnObj.cki_replace_value || ''}
                  disabled={readOnly}
                  data-type='cookie_operation'
                  placeholder='Value'
                  onChange={e => onInputChange(e, 'cki_replace_value', operationIndex)}
                />
              </InputGroup>
            </Form.Group>
          </div>
        );
        break;
      case 'Delete':
      case 'Create':
        logicSelections.push(
          <div key={logicSelections.length} className='logic-selection'>
            <Form.Group as={Col}>
              <InputGroup>
                <Form.Control
                  value={utmOptrnObj.cookie_key || ''}
                  disabled={readOnly}
                  data-type='cookie_operation'
                  placeholder='Key'
                  onChange={e => onInputChange(e, 'cookie_key', operationIndex)}
                />
                <Form.Control
                  value={utmOptrnObj.cookie_value || ''}
                  disabled={readOnly}
                  data-type='cookie_operation'
                  placeholder='Value'
                  onChange={e => onInputChange(e, 'cookie_value', operationIndex)}
                />
              </InputGroup>
            </Form.Group>
          </div>
        );
        break;
      default:
        break;
    }
    return logicSelections;
  };

  const renderCookieVariants = () => {
    // Plover.variants is only weakly typed (Record<string, any>[]) in bpm-types; every
    // variant read off wtoFormObj is actually WtoVariant-shaped at runtime.
    const cookieOperationUrls = getCookieOperationUrls(
      newRule.variant_splits,
      wtoFormObj.variants as WtoVariant[]
    );
    const variantOptions = [
      {
        cookie_variant_id: 'All'
      },
      ...cookieOperationUrls
    ];
    return (
      <div className='operation-section utmSection'>
        <div className='variant-wrapper'>
          Select Variant for Cookie Operations
          <DropdownButton
            className='condition-dropdown'
            disabled={readOnly}
            title={
              newRule.cookie_operation_url.length === 0 || newRule.cookie_operation_url.length > 1
                ? 'All'
                : newRule.cookie_operation_url[0].cookie_variant_id
            }
          >
            {variantOptions.map(variant => (
              <Dropdown.Item
                key={variant.cookie_variant_id}
                eventKey={variant.cookie_variant_id}
                onClick={() => {
                  const newRuleFormObj = R.clone(newRule);
                  if (variant.cookie_variant_id === 'All') {
                    newRuleFormObj.cookie_operation_url = cookieOperationUrls;
                  } else {
                    newRuleFormObj.cookie_operation_url = [
                      // variant is itself drawn from cookieOperationUrls here, so a match always exists.
                      cookieOperationUrls.find(
                        url => url.cookie_variant_id === variant.cookie_variant_id
                      )!
                    ];
                  }
                  setNewRule(newRuleFormObj);
                }}
              >
                {variant.cookie_variant_id}
              </Dropdown.Item>
            ))}
          </DropdownButton>
        </div>
      </div>
    );
  };

  const renderCookie = () => (
    <div className='operation-section cookieSection'>
      {newRule.cookie_operation.length > 0 && (
        <div className='operation-wrapper'>
          {newRule.cookie_operation.map((_: unknown, idj: number) => (
            <div className='each-operation' key={idj}>
              <DropdownButton
                disabled={readOnly}
                className='condition-dropdown'
                title={
                  newRule.cookie_operation[idj].operation
                    ? newRule.cookie_operation[idj].operation
                    : 'Select Operation'
                }
              >
                {COOKIE_OPERATION.map(cookieOperation => (
                  <Dropdown.Item
                    key={cookieOperation}
                    eventKey={cookieOperation}
                    onClick={() => onSelectChange(cookieOperation, 'cookie_operation', idj)}
                  >
                    {cookieOperation}
                  </Dropdown.Item>
                ))}
              </DropdownButton>

              {newRule.cookie_operation[idj].operation &&
                newRule.cookie_operation[idj].operation.length > 0 &&
                renderCookieLogic(newRule.cookie_operation[idj], idj)}
              {!readOnly && (
                <IconButton
                  data-index={idj}
                  onClick={e => {
                    e.preventDefault();
                    onDeleteOperationClick(e, 'cookie_operation');
                  }}
                >
                  <MdDelete />
                </IconButton>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Row>
      <Form.Group as={Col} className='rule-editor utm-cookie-wrapper'>
        <span className='rule-header'>Cookie Operations</span>
        {renderCookieVariants()}
        {renderCookie()}
        <Button
          size={Size.LG}
          disabled={readOnly}
          className='add-rule'
          leadingElement={<HiOutlinePlus />}
          onClick={e => {
            e.preventDefault();
            addParameterUpdatesRow('cookie_operation');
          }}
        >
          Add Row
        </Button>
      </Form.Group>
    </Row>
  );
};
